import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../../../src/firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged, setPersistence, browserLocalPersistence } from 'firebase/auth';

function SearchForm() {
  const [loading, setLoading] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiFullResponse, setAiFullResponse] = useState('');
  const [aiDisplayedResponse, setAiDisplayedResponse] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const typingSpeed = 10;

  useEffect(() => {
    setPersistence(auth, browserLocalPersistence)
      .catch(error => console.error('Persistence setup failed:', error));
  }, []);

  const handleCancel = () => {
    setIsModalOpen(false);
    setAiFullResponse('');
    setAiDisplayedResponse('');
    setIsTyping(false);
    setLoading(false);
    setError(null);
    setAiQuery('');
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleEscapeKey = useCallback((e) => {
    if (e.key === 'Escape') {
      handleCloseModal();
    }
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isModalOpen, handleEscapeKey]);

  useEffect(() => {
    let index = 0;
    if (aiFullResponse) {
      setAiDisplayedResponse('');
      setIsTyping(true);
      const interval = setInterval(() => {
        setAiDisplayedResponse((prev) => prev + aiFullResponse.charAt(index));
        index++;
        if (index >= aiFullResponse.length) {
          clearInterval(interval);
          setIsTyping(false);
        }
      }, typingSpeed);
      return () => clearInterval(interval);
    }
  }, [aiFullResponse]);

  const handleAiSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    let retryCount = 0;
  
    const makeRequest = async () => {
      const user = auth.currentUser;
      if (!user) throw new Error('Du skal være logget ind');
  
      const token = await user.getIdToken(true);
      if (retryCount > 0) {
        await new Promise(r => setTimeout(r, 1000 * retryCount));
      }
  
      const response = await fetch('https://investorverden.onrender.com/api/ai-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          query: aiQuery,
          userId: user.uid
        })
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        console.log('Request attempt', retryCount + 1, 'failed:', {
          status: response.status,
          error: errorData
        });
  
        if (response.status === 401 && retryCount < 2) {
          retryCount++;
          return makeRequest();
        }
        throw new Error('Kunne ikke behandle din forespørgsel - prøv igen senere');
      }
  
      return response.json();
    };
  
    try {
      const data = await makeRequest();
      setAiFullResponse(data.response);
    } catch (error) {
      console.error('AI Analysis Error:', error);
      setError(error.message);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert('Du skal være logget ind for at gemme analyser');
        return;
      }
  
      const analysisRef = await addDoc(collection(db, 'stockAnalyses'), {
        query: aiQuery,
        analysis: aiFullResponse,
        userId: user.uid,
        timestamp: serverTimestamp()
      });
  
      const userTabsRef = collection(db, 'users', user.uid, 'tabs');
      await addDoc(userTabsRef, {
        type: 'analysis',
        analysisId: analysisRef.id,
        query: aiQuery,
        timestamp: serverTimestamp()
      });
  
      setIsModalOpen(false); 
    } catch (error) {
      console.error('Error saving analysis:', error);
      alert('Fejl ved gemning af analyse: ' + error.message);
    }
  };

  const handleClickOutside = (e) => {
    if (e.target.classList.contains('modal-overlay')) {
      setIsModalOpen(false);
    }
  };

  return (
    <div className="w-full xl:w-72">
      <div className="lg:sticky lg:top-16 lg:h-[calc(100dvh-64px)] lg:overflow-x-hidden lg:overflow-y-auto no-scrollbar">
        <div className="py-4 md:py-8">
          <div className="px-4 mb-6">
            <button
              onClick={handleOpenModal}
              className="btn w-full bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white"
            >
              Bed Ai om en aktieanalyse
            </button>
          </div>

          {isModalOpen && (
            <>
              <div 
                className="fixed inset-0 bg-gray-900 bg-opacity-30 z-[60] modal-overlay"
                onClick={handleClickOutside}
              />
              
              <div 
                className="fixed inset-0 z-[70] flex items-center justify-center px-4 sm:px-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full sm:max-w-4xl max-h-full overflow-auto relative">  
                  <div className="p-6">
                    <button
                      className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                      onClick={() => setIsModalOpen(false)}
                    >
                      <svg className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                      </svg>
                    </button>
                    
                    <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
                      Ai Aktieanalyse
                    </h2>

                    <form onSubmit={handleAiSubmit}>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">
                            Hvilken aktie vil du analysere?
                          </label>
                          <input
                            id="ai-query"
                            className="form-input w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                            type="text"
                            placeholder="Indtast aktiens navn eller symbol"
                            value={aiQuery}
                            onChange={(e) => setAiQuery(e.target.value)}
                            required
                          />
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="submit"
                            className="btn-sm bg-violet-500 hover:bg-violet-600 text-white"
                            disabled={loading}
                          >
                            {loading ? 'Søger internettet...' : 'Send forespørgsel'}
                          </button>
                        </div>
                      </div>
                    </form>

                    {aiFullResponse && (
                      <div className="mt-6 space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                          Analyse:
                        </h3>
                        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg text-gray-800 dark:text-gray-200 whitespace-pre-line">
                          {isTyping && (
                            <div className="flex justify-center mb-2">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" role="status" aria-label="Loading"></div>
                            </div>
                          )}
                          {aiDisplayedResponse}
                          {aiDisplayedResponse.length < aiFullResponse.length && (
                            <span className="animate-pulse">|</span>
                          )}
                        </div>
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={handleCancel}
                            className="btn-sm bg-gray-500 hover:bg-gray-600 text-white"
                          >
                            Annuller
                          </button>
                          <button
                            onClick={handleSave}
                            className="btn-sm bg-green-500 hover:bg-green-600 text-white"
                          >
                            Gem analyse
                          </button>
                        </div>
                      </div>
                    )}

                    {loading && !aiFullResponse && (
                      <div className="mt-6 flex justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" role="status" aria-label="Loading"></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SearchForm;